<script setup lang="ts">
import * as THREE from 'three'
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue'
import { usePotreeEngine } from '../hooks'
import {
  applySpaceMouseNavigationProfile,
  applySpaceMouseSecondaryControlSelection,
  buildSpaceMouseSecondaryControlGroups,
  createSpaceMouseNavigationState,
  readSpaceMouseNavigationState,
  type SpaceMouseControllerLike,
  type SpaceMouseNavigationProfile,
  type SpaceMouseNavigationState,
} from '../utils/spaceMouseNavigation'
import GraphicsBackendControlCard from './GraphicsBackendControlCard.vue'
import SceneAssetLoaderCard from './SceneAssetLoaderCard.vue'
import SceneClipCard from './SceneClipCard.vue'
import SceneControlPanel from './SceneControlPanel.vue'
import SceneGroupCard from './SceneGroupCard.vue'
import { downloadBlob } from '../utils/downloadBlob'
import SceneMeasureCard from './SceneMeasureCard.vue'
import ScenePointBudgetCard from './ScenePointBudgetCard.vue'
import ScenePointCloudCard from './ScenePointCloudCard.vue'
import SceneStatusCard from './SceneStatusCard.vue'
import SceneViewCard from './SceneViewCard.vue'
import { useExampleGraphicsCard } from '../composables/useExampleGraphicsCard'
import { resolvePointCloudInputPath } from '../utils/assetPath'
import { toGraphicsRuntimeError } from '../utils/graphicsRuntimeError'
import {
  readPointCloudDemoUrlConfig,
  reloadPointCloudDemoConfig,
  type PointCloudDemoUrlPatch,
  type PointDrawMode,
} from '../utils/pointCloudDemoUrlConfig'
import {
  CameraMode,
  ClipTask,
  ClipOverlayPlugin,
  ClipPlugin,
  ControlPlugin,
  type ControlMode,
  Gradients,
  MeasureOverlayPlugin,
  MeasurePlugin,
  type OrbitElasticLimits,
  type OrbitControllerSettings,
  PointCloudPlugin,
  PointShape,
  PointSizeType,
  presets,
  type ClipService,
  type MeasureService,
  type MeasureType,
  type PointCloudService,
  type PotreeEngine,
  type ViewPreset,
} from '@sharefe/share3d-engine'

type ClipSelectionMode = 'polygon' | 'rectangle' | 'box3d'
type ClipApplyMode = 'inside' | 'outside'
type PointSizingMode = 'adaptive' | 'fixed'
type PointShapeMode = 'square' | 'circle'
type PointRenderMode = 'rgba' | 'elevation' | 'intensity' | 'gps_time' | 'color'
type ElevationGradientKey = keyof typeof Gradients
type ElevationGradientStops = (typeof Gradients)[ElevationGradientKey]
type ClipController = ClipService & {
  setSelectionMode?: (mode: ClipSelectionMode) => void
  getSelectionMode?: () => ClipSelectionMode
  setClipMode?: (mode: ClipSelectionMode) => void
  getClipMode?: () => ClipSelectionMode
  setClipApplyMode?: (mode: ClipApplyMode) => void
  getClipApplyMode?: () => ClipApplyMode
  confirmDraft?: () => boolean
  clearDraft?: () => void
  clearVolumes?: () => void
}
type PointCloudMaterialLike = {
  size: number
  pointSizeType: number
  shape: number
  activeAttributeName: string | null
  useEDL: boolean
  opacity: number
  color: THREE.Color
  gradient?: ElevationGradientStops
  elevationRange?: [number, number]
  intensityRange?: [number, number]
  activeSamplingRange?: [number, number]
}
type PointCloudObjectLike = THREE.Object3D & {
  material?: PointCloudMaterialLike
}
type ElasticOrbitController = {
  setElasticLimits(limits?: OrbitElasticLimits): void
}

const DEFAULT_FIXED_POINT_SIZE = 1
const DEFAULT_POINT_CLOUD_MODEL = '/converted_r/metadata.json'
const SINGLE_COLOR = new THREE.Color('#f2f7ff')
const DEFAULT_SINGLE_COLOR_HEX = '#f2f7ff'
const POTREE_MAX_VIEW_RADIUS_RATIO = 1.5
const POTREE_ORBIT_MAX_ZOOM = 4
const POTREE_ORBIT_MIN_ZOOM_OVERSCROLL_RATIO = 0.2
const POTREE_ORBIT_MAX_ZOOM_OVERSCROLL = 1
const POINT_COLOR_SWATCHES = [
  '#f2f7ff',
  '#ffffff',
  '#ffd166',
  '#ff7b72',
  '#7ee787',
  '#6cb6ff',
  '#c297ff',
  '#94a3b8',
]

const MEASURE_OPTIONS: Array<{
  value: MeasureType
  label: string
  detail: string
}> = [
  { value: 'coordinate', label: '坐标', detail: '单点坐标，1 点自动完成' },
  { value: 'distance', label: '距离', detail: '实时预览，右键结束' },
  { value: 'height', label: '高度', detail: '2 点自动完成' },
  { value: 'circle', label: '圆', detail: '3 点拟合圆' },
  { value: 'azimuth', label: '方位角', detail: '2 点自动完成' },
  { value: 'angle', label: '角度', detail: '建议 3 点成角，右键结束' },
  { value: 'area', label: '面积', detail: '3 点以上闭合，右键结束' },
]

const CLIP_SELECTION_OPTIONS: Array<{
  value: ClipSelectionMode
  label: string
  detail: string
}> = [
  { value: 'polygon', label: '多边形', detail: '逐点圈选后确认裁切' },
  { value: 'rectangle', label: '矩形', detail: '两点框选后确认裁切' },
  { value: 'box3d', label: '3D Box', detail: '默认包围盒，可拖动操控' },
]

const CLIP_APPLY_OPTIONS: Array<{
  value: ClipApplyMode
  label: string
  detail: string
}> = [
  { value: 'inside', label: '内裁', detail: '保留选中的范围' },
  { value: 'outside', label: '外裁', detail: '保留未选中的范围' },
]

const VIEW_OPTIONS: Array<{
  value: ViewPreset
  label: string
}> = [
  { value: 'top', label: '俯视' },
  { value: 'bottom', label: '仰视' },
  { value: 'front', label: '前视' },
  { value: 'back', label: '后视' },
  { value: 'left', label: '左视' },
  { value: 'right', label: '右视' },
]

const CONTROL_MODE_OPTIONS: Array<{
  value: ControlMode
  label: string
  detail: string
}> = [
  {
    value: 'orbit',
    label: '轨道',
    detail: '正交缩小到模型包围球 1.5 倍时产生阻力，越界后回弹',
  },
  { value: 'spacemouse', label: 'SpaceMouse', detail: '对象中心旋转，同时兼容普通鼠标与 3D 鼠标' },
  { value: 'earth', label: '地球', detail: '左键命中场景点后围绕该点旋转' },
  { value: 'map', label: '地图', detail: '适合平移浏览与地图式交互' },
  { value: 'first-person', label: '第一人称（Three）', detail: '保留 three 官方 FirstPersonControls 手感' },
  { value: 'legacy-first-person', label: '第一人称（旧版）', detail: '左键拖拽旋转、右键平移的旧版漫游' },
  { value: 'fly', label: '飞行（Three）', detail: 'three 官方 FlyControls，自由飞行与拖拽视角' },
]

const SPACEMOUSE_DEFAULT_PROFILE: SpaceMouseNavigationProfile = 'mouse-only'

const POTREE_ORBIT_SETTINGS = {
  elasticLimits: {
    polar: {
      min: THREE.MathUtils.degToRad(12),
      max: THREE.MathUtils.degToRad(168),
      minOverscroll: THREE.MathUtils.degToRad(4),
      maxOverscroll: THREE.MathUtils.degToRad(8),
    },
    zoom: {
      min: 0.5,
      max: POTREE_ORBIT_MAX_ZOOM,
      minOverscroll: 0.5 * POTREE_ORBIT_MIN_ZOOM_OVERSCROLL_RATIO,
      maxOverscroll: POTREE_ORBIT_MAX_ZOOM_OVERSCROLL,
    },
    resistance: 0.22,
    returnRate: 9,
    wheelIdleMs: 140,
  },
} satisfies OrbitControllerSettings

function applyPointCloudOrbitZoomLimits(engineInstance: PotreeEngine, bounds: THREE.Box3) {
  if (bounds.isEmpty()) return

  const sphere = bounds.getBoundingSphere(new THREE.Sphere())
  const cameraDistance = engineInstance.camera.position.distanceTo(engineInstance.camera.target)
  const halfFov = THREE.MathUtils.degToRad(engineInstance.camera.fov) / 2
  const fittedHalfHeight = cameraDistance * Math.tan(halfFov)
  const minZoom = fittedHalfHeight / (sphere.radius * POTREE_MAX_VIEW_RADIUS_RATIO)

  if (!Number.isFinite(minZoom) || minZoom <= 0 || minZoom >= POTREE_ORBIT_MAX_ZOOM) return

  const orbitController = engineInstance.controls.getController('orbit') as
    | ElasticOrbitController
    | undefined
  orbitController?.setElasticLimits({
    ...POTREE_ORBIT_SETTINGS.elasticLimits,
    zoom: {
      min: minZoom,
      max: POTREE_ORBIT_MAX_ZOOM,
      minOverscroll: minZoom * POTREE_ORBIT_MIN_ZOOM_OVERSCROLL_RATIO,
      maxOverscroll: POTREE_ORBIT_MAX_ZOOM_OVERSCROLL,
    },
  })
}

const POINT_SIZING_OPTIONS: Array<{
  value: PointSizingMode
  label: string
  detail: string
}> = [
  { value: 'adaptive', label: '自适应', detail: '按视距自动调节点大小' },
  { value: 'fixed', label: '固定', detail: '使用统一点大小渲染' },
]

const POINT_SHAPE_OPTIONS: Array<{
  value: PointShapeMode
  label: string
  detail: string
}> = [
  { value: 'square', label: '方形', detail: '默认方形点' },
  { value: 'circle', label: '圆形', detail: '圆形点，边缘更平滑' },
]

const POINT_RENDER_OPTIONS: Array<{
  value: PointRenderMode
  label: string
  detail: string
}> = [
  { value: 'rgba', label: 'RGB 模式', detail: '使用点云原始颜色' },
  { value: 'elevation', label: '高程模式', detail: '按高程渐变着色' },
  { value: 'intensity', label: '强度模式', detail: '按强度值映射亮度' },
  { value: 'gps_time', label: '时间模式', detail: '按 gps_time 渐变着色' },
  { value: 'color', label: '单色模式', detail: '统一使用单色显示' },
]

const ELEVATION_GRADIENT_LABELS: Record<ElevationGradientKey, string> = {
  RAINBOW: '彩虹',
  SPECTRAL: '光谱',
  PLASMA: 'PLASMA',
  YELLOW_GREEN: '黄绿',
  VIRIDIS: 'VIRIDIS',
  TURBO: 'TURBO',
  INFERNO: 'INFERNO',
  GRAYSCALE: '灰度',
  CONTOUR: '等高线',
}

const UI_GRADIENT_OPTIONS: Array<{
  value: ElevationGradientKey
  label: string
}> = [
  { value: 'RAINBOW', label: '彩虹' },
  { value: 'SPECTRAL', label: '光谱' },
  { value: 'PLASMA', label: 'PLASMA' },
  { value: 'YELLOW_GREEN', label: '黄绿' },
  { value: 'VIRIDIS', label: 'VIRIDIS' },
  { value: 'TURBO', label: 'TURBO' },
  { value: 'INFERNO', label: 'INFERNO' },
  { value: 'GRAYSCALE', label: '灰度' },
  { value: 'CONTOUR', label: '等高线' },
]

const containerRef = useTemplateRef('containerRef')

const plugins = [
  ...presets
    .pointCloudOnly()
    .map((plugin) =>
      plugin.name === 'control'
        ? new ControlPlugin({ orbit: POTREE_ORBIT_SETTINGS })
        : plugin,
    ),
  new MeasurePlugin(),
  new MeasureOverlayPlugin(),
  new ClipPlugin(),
  new ClipOverlayPlugin(),
]

const pointCloudDemoConfig = readPointCloudDemoUrlConfig()
const cameraMode = ref<CameraMode>(CameraMode.Perspective)
const controlMode = ref<ControlMode>('spacemouse')
const spaceMouseNavigation = ref<SpaceMouseNavigationState>(
  createSpaceMouseNavigationState(SPACEMOUSE_DEFAULT_PROFILE),
)
const activeTool = ref<string | null>(null)
const measureType = ref<MeasureType>('distance')
const clipSelectionMode = ref<ClipSelectionMode>('polygon')
const clipApplyMode = ref<ClipApplyMode>('inside')
const clipCanConfirm = ref(false)
const measureCount = ref(0)
const clipCount = ref(0)
const pointCloudAssetCount = ref(0)
const pointCloudTotalPoints = ref(0)
const pointCloudVisiblePoints = ref(0)
const pointCloudVisibleNodes = ref(0)
const pointBudget = ref(0)
const pointCloudInput = ref(DEFAULT_POINT_CLOUD_MODEL)
const pointCloudLoading = ref(false)
const pointCloudLoadError = ref('')
const pointSizingMode = ref<PointSizingMode>('adaptive')
const pointShapeMode = ref<PointShapeMode>('square')
const pointSize = ref(DEFAULT_FIXED_POINT_SIZE)
const pointRenderMode = ref<PointRenderMode>('rgba')
const pointCloudUseEDL = ref(false)
const pointCloudOpacity = ref(1)
const pointDrawMode = ref<PointDrawMode>(pointCloudDemoConfig.pointDrawMode)
const groupedDrawPointLimit = ref(pointCloudDemoConfig.groupedDrawPointLimit)
const drawCallColorEnabled = ref(false)
const pointElevationGradient = ref<ElevationGradientKey>('SPECTRAL')
const pointElevationBounds = ref<[number, number]>([0, 1])
const pointElevationRange = ref<[number, number]>([0, 1])
const pointIntensityGradient = ref<ElevationGradientKey>('SPECTRAL')
const pointIntensityBounds = ref<[number, number]>([0, 1])
const pointIntensityRange = ref<[number, number]>([0, 1])
const pointGpsGradient = ref<ElevationGradientKey>('SPECTRAL')
const pointGpsBounds = ref<[number, number]>([0, 1])
const pointGpsRange = ref<[number, number]>([0, 1])
const pointSingleColor = ref(DEFAULT_SINGLE_COLOR_HEX)
let pointSizeCustomized = false
let spaceMouseDefaultApplied = false

const cleanupTasks: Array<() => void> = []

/**
 * 该 route 的插件闭包为 corePlugins() + Clip/Measure 全家桶，已全部翻为双后端；
 * 后端来源统一取图形控制卡的 requestedBackend。
 */
const graphicsCard = useExampleGraphicsCard({
  presetKey: 'pointCloudOnly',
  extras: ['MeasurePlugin', 'MeasureOverlayPlugin', 'ClipPlugin', 'ClipOverlayPlugin'],
})

const { engine, init, actualBackend } = usePotreeEngine(containerRef, {
  backend: graphicsCard.requestedBackend.value,
  plugins,
  renderMode: 'on-demand',
  background: 'black',
  pointBudget: pointCloudDemoConfig.pointBudget,
  fov: 60,
  cameraMode: CameraMode.Perspective,
  controlMode: 'spacemouse',
})

const activeToolLabel = computed(() => {
  if (activeTool.value === 'measure') {
    return '测量工具'
  }

  if (activeTool.value === 'clip') {
    return '裁切工具'
  }

  return '未启用'
})

const cameraModeLabel = computed(() => {
  return cameraMode.value === CameraMode.Perspective ? '透视' : '正交'
})

const pointCloudLoaderHint = computed(() => {
  if (pointCloudLoadError.value) {
    return `加载失败：${pointCloudLoadError.value}`
  }

  return '请选择包含 metadata.json 的本地目录，然后点击“加载模型”。'
})

const activeMeasureDetail = computed(() => {
  return MEASURE_OPTIONS.find((item) => item.value === measureType.value)
})

const activeClipSelectionDetail = computed(() => {
  return CLIP_SELECTION_OPTIONS.find((item) => item.value === clipSelectionMode.value)
})

const activeClipApplyDetail = computed(() => {
  return CLIP_APPLY_OPTIONS.find((item) => item.value === clipApplyMode.value)
})

const isLiveBoxClipMode = computed(() => clipSelectionMode.value === 'box3d')

const activePointSizingDetail = computed(() => {
  return POINT_SIZING_OPTIONS.find((item) => item.value === pointSizingMode.value)
})

const activePointShapeDetail = computed(() => {
  return POINT_SHAPE_OPTIONS.find((item) => item.value === pointShapeMode.value)
})

const activePointRenderDetail = computed(() => {
  return POINT_RENDER_OPTIONS.find((item) => item.value === pointRenderMode.value)
})

const activeElevationGradientDetail = computed(() => {
  return UI_GRADIENT_OPTIONS.find(
    (item) => item.value === pointElevationGradient.value,
  )
})

const activeElevationGradientCss = computed(() => {
  return toGradientCss(Gradients[pointElevationGradient.value])
})

const activeIntensityGradientDetail = computed(() => {
  return UI_GRADIENT_OPTIONS.find(
    (item) => item.value === pointIntensityGradient.value,
  )
})

const activeIntensityGradientCss = computed(() => {
  return toGradientCss(Gradients[pointIntensityGradient.value])
})

const activeGpsGradientDetail = computed(() => {
  return UI_GRADIENT_OPTIONS.find((item) => item.value === pointGpsGradient.value)
})

const activeGpsGradientCss = computed(() => {
  return toGradientCss(Gradients[pointGpsGradient.value])
})

const elevationRangeStep = computed(() => {
  const span = pointElevationBounds.value[1] - pointElevationBounds.value[0]

  if (!Number.isFinite(span) || span <= 0) {
    return 0.01
  }

  return Math.max(0.01, span / 500)
})

const intensityRangeStep = computed(() => {
  const span = pointIntensityBounds.value[1] - pointIntensityBounds.value[0]

  if (!Number.isFinite(span) || span <= 0) {
    return 1
  }

  return Math.max(1, span / 500)
})

const gpsRangeStep = computed(() => {
  const span = pointGpsBounds.value[1] - pointGpsBounds.value[0]

  if (!Number.isFinite(span) || span <= 0) {
    return 0.01
  }

  return Math.max(0.01, span / 500)
})

const panelHint = computed(() => {
  if (activeTool.value === 'measure') {
    if (measureType.value === 'coordinate') {
      return '当前测量：坐标。移动鼠标会高亮候选点，左键单击即可记录该点的三维坐标。'
    }

    return `当前测量：${activeMeasureDetail.value?.label ?? '距离'}。移动鼠标会高亮候选点，左键加点并实时预览，右键结束当前测量；若仅有一个点则会取消。`
  }

  if (activeTool.value === 'clip') {
    if (clipSelectionMode.value === 'box3d') {
      return '当前裁切：3D Box。进入后会默认实时内裁，拖动 gizmo 会立即更新裁切结果。'
    }

    return `当前裁切：${activeClipSelectionDetail.value?.label ?? '多边形'} / ${activeClipApplyDetail.value?.label ?? '内裁'}。先在上面选方式，再在视口里绘制草稿，最后点击“确认裁切”。`
  }

  return '先从右侧选择工具，再在点云上直接交互。'
})

const statusItems = computed(() => {
  return [
    { label: '当前工具', value: activeToolLabel.value },
    { label: '相机模式', value: cameraModeLabel.value },
    {
      label: '控制器',
      value:
        CONTROL_MODE_OPTIONS.find((item) => item.value === controlMode.value)?.label ??
        controlMode.value,
    },
    { label: '测量结果', value: measureCount.value },
    { label: '裁切体', value: clipCount.value },
  ]
})

const pointCloudCardBadge = computed(() => {
  return `${activePointShapeDetail.value?.label ?? '方形'} / ${activePointSizingDetail.value?.label ?? '自适应'} / ${activePointRenderDetail.value?.label ?? 'RGB 模式'}`
})

const clipCardBadge = computed(() => {
  if (isLiveBoxClipMode.value) {
    return activeClipSelectionDetail.value?.label ?? '3D Box'
  }

  return `${activeClipSelectionDetail.value?.label ?? '多边形'} / ${activeClipApplyDetail.value?.label ?? '内裁'}`
})

const clipCardDescription = computed(() => {
  if (isLiveBoxClipMode.value) {
    return '3D Box 默认按点云包围盒初始化，并直接进入实时内裁。拖动 gizmo 时会立即更新裁切结果，不需要再确认。'
  }

  return '第一行选择裁切形状，第二行选择保留方式。草稿准备好后点击“确认裁切”才会真正生效。'
})

function getMeasureService(): MeasureService | null {
  const engineInstance = engine.value

  if (!engineInstance || !engineInstance.context.hasService('MeasureService')) {
    return null
  }

  return engineInstance.context.getService('MeasureService')
}

function getClipController(): ClipController | null {
  const engineInstance = engine.value

  if (!engineInstance || !engineInstance.context.hasService('ClipService')) {
    return null
  }

  return engineInstance.context.getService('ClipService') as ClipController
}

function getPointCloudService(): PointCloudService | null {
  const engineInstance = engine.value

  if (!engineInstance || !engineInstance.context.hasService('PointCloudService')) {
    return null
  }

  return engineInstance.context.getService('PointCloudService')
}

const screenshotBusy = ref(false)
const screenshotNotice = ref('')

/** 导出当前主视口截图；走引擎 screenshot()，WebGPU 下由内部 capture/readback 合成。 */
async function captureScreenshot(): Promise<void> {
  const engineInstance = engine.value
  if (!engineInstance || screenshotBusy.value) {
    return
  }
  screenshotBusy.value = true
  screenshotNotice.value = ''
  try {
    const blob = await engineInstance.screenshot({ mimeType: 'image/png' })
    if (blob.size === 0) {
      throw new Error('截图结果为空')
    }
    const backendLabel = actualBackend.value ?? 'unknown'
    downloadBlob(blob, `potree-${backendLabel}-${Date.now()}.png`)
    screenshotNotice.value = `已导出 ${backendLabel} 截图（${Math.round(blob.size / 1024)} KB）`
  } catch (error) {
    screenshotNotice.value = error instanceof Error ? `截图失败：${error.message}` : '截图失败'
  } finally {
    screenshotBusy.value = false
  }
}

function getPointCloudPlugin(): PointCloudPlugin | null {
  return engine.value?.plugins.get<PointCloudPlugin>('pointCloud') ?? null
}

function getSpaceMouseController(): SpaceMouseControllerLike | null {
  const controller = engine.value?.controls.getController('spacemouse') as
    | SpaceMouseControllerLike
    | undefined

  return controller ?? null
}

function syncSpaceMouseNavigationState() {
  spaceMouseNavigation.value = readSpaceMouseNavigationState(
    getSpaceMouseController(),
    SPACEMOUSE_DEFAULT_PROFILE,
  )
}

function ensureSpaceMouseDefaultProfile() {
  if (spaceMouseDefaultApplied) {
    return
  }

  const controller = getSpaceMouseController()
  if (!controller) {
    return
  }

  applySpaceMouseNavigationProfile(controller, SPACEMOUSE_DEFAULT_PROFILE)
  spaceMouseDefaultApplied = true
  syncSpaceMouseNavigationState()
}

function getPointCloudMaterials(): PointCloudMaterialLike[] {
  const pointCloudService = getPointCloudService()

  if (!pointCloudService) {
    return []
  }

  return pointCloudService.getAll().flatMap((handle) => {
    const object3D = handle.object3D as PointCloudObjectLike
    return object3D.material ? [object3D.material] : []
  })
}

function normalizePointRenderMode(value: string | null | undefined): PointRenderMode {
  switch (value) {
    case 'elevation':
    case 'intensity':
    case 'intensity gradient':
    case 'gps_time':
    case 'color':
      return value === 'intensity gradient' ? 'intensity' : value
    case 'rgba':
    default:
      return 'rgba'
  }
}

function resolvePointSizeType(mode: PointSizingMode): number {
  return mode === 'fixed' ? PointSizeType.FIXED : PointSizeType.ADAPTIVE
}

function resolvePointShape(mode: PointShapeMode): number {
  return mode === 'circle' ? PointShape.CIRCLE : PointShape.SQUARE
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function normalizeElevationBounds(range: [number, number]): [number, number] {
  let min = Number(range[0])
  let max = Number(range[1])

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return [0, 1]
  }

  if (min > max) {
    ;[min, max] = [max, min]
  }

  if (min === max) {
    max = min + 1
  }

  return [min, max]
}

function clampElevationRange(
  range: [number, number],
  bounds: [number, number],
): [number, number] {
  const [boundMin, boundMax] = normalizeElevationBounds(bounds)
  let min = clamp(Number(range[0]), boundMin, boundMax)
  let max = clamp(Number(range[1]), boundMin, boundMax)

  if (!Number.isFinite(min)) {
    min = boundMin
  }

  if (!Number.isFinite(max)) {
    max = boundMax
  }

  if (min > max) {
    ;[min, max] = [max, min]
  }

  return [min, max]
}

/**
 * URL 参数切换点绘制链路：`#/standalone/potree?backend=webgpu&pointDraw=batched`。
 *
 * `batched` 切换显式 grouped 策略；WebGPU 默认策略同样使用分组链路，基础几何固定包含 128 个点三角形，
* `instanceCount = ceil(点数 / 128)`。`batchPoints` 仅为旧 URL 兼容保留，不再参与切批。
 * 切换后建议重新加载模型（点云几何会按新链路重新打包）。
 */
function applyPointDrawModeFromUrl() {
  const configured = readPointCloudDemoUrlConfig()
  const mode = actualBackend.value === 'webgpu' ? configured.pointDrawMode : 'node'
  setPointDrawMode(mode)
}

function setPointDrawMode(mode: PointDrawMode) {
  const plugin = getPointCloudPlugin()
  if (!plugin) return
  pointDrawMode.value = mode
  plugin.setConfig({
    renderStrategy: mode === 'grouped' ? 'batched-sprite' : 'default',
    batchPointBudget: groupedDrawPointLimit.value,
  })
}

function reloadPointCloudDemo(patch: PointCloudDemoUrlPatch) {
  reloadPointCloudDemoConfig(patch)
}

function setDrawCallColorEnabled(enabled: boolean) {
  drawCallColorEnabled.value = enabled
  getPointCloudPlugin()?.setConfig({ groupedDrawCallColor: enabled })
}

function getPointCloudHandles() {
  return getPointCloudService()?.getAll() ?? []
}

function getMaterialElevationRange(material: PointCloudMaterialLike | undefined): [number, number] | null {
  if (!material?.elevationRange || material.elevationRange.length < 2) {
    return null
  }

  if (
    !Number.isFinite(material.elevationRange[0]) ||
    !Number.isFinite(material.elevationRange[1])
  ) {
    return null
  }

  return normalizeElevationBounds([material.elevationRange[0], material.elevationRange[1]])
}

function getMaterialIntensityRange(material: PointCloudMaterialLike | undefined): [number, number] | null {
  if (!material?.intensityRange || material.intensityRange.length < 2) {
    return null
  }

  if (
    !Number.isFinite(material.intensityRange[0]) ||
    !Number.isFinite(material.intensityRange[1])
  ) {
    return null
  }

  return normalizeElevationBounds([material.intensityRange[0], material.intensityRange[1]])
}

function resolveGradientKey(gradient: ElevationGradientStops | undefined): ElevationGradientKey {
  if (!gradient) {
    return 'SPECTRAL'
  }

  for (const key of Object.keys(Gradients) as ElevationGradientKey[]) {
    const candidate = Gradients[key]

    if (candidate === gradient) {
      return key
    }

    if (
      candidate.length === gradient.length &&
      candidate.every(([offset, color], index) => {
        const current = gradient[index]
        return (
          Math.abs(offset - current[0]) < 1e-6 &&
          color.getHexString() === current[1].getHexString()
        )
      })
    ) {
      return key
    }
  }

  return 'SPECTRAL'
}

function toGradientCss(gradient: ElevationGradientStops): string {
  const stops = gradient.map(([offset, color]) => `${color.getStyle()} ${Math.round(offset * 100)}%`)
  return `linear-gradient(90deg, ${stops.join(', ')})`
}

function normalizeRangeWithinBounds(
  range: [number, number],
  bounds: [number, number],
): [number, number] {
  const [boundMin, boundMax] = normalizeElevationBounds(bounds)
  const [rangeMin, rangeMax] = clampElevationRange(range, [boundMin, boundMax])
  const span = boundMax - boundMin

  if (!Number.isFinite(span) || span <= 0) {
    return [0, 1]
  }

  return [
    clamp((rangeMin - boundMin) / span, 0, 1),
    clamp((rangeMax - boundMin) / span, 0, 1),
  ]
}

function denormalizeRangeWithinBounds(
  normalizedRange: [number, number],
  bounds: [number, number],
): [number, number] {
  const [boundMin, boundMax] = normalizeElevationBounds(bounds)
  const span = boundMax - boundMin

  if (!Number.isFinite(span) || span <= 0) {
    return [boundMin, boundMax]
  }

  return clampElevationRange(
    [
      boundMin + clamp(normalizedRange[0], 0, 1) * span,
      boundMin + clamp(normalizedRange[1], 0, 1) * span,
    ],
    [boundMin, boundMax],
  )
}

function resolvePointCloudElevationBounds(): [number, number] | null {
  let min = Number.POSITIVE_INFINITY
  let max = Number.NEGATIVE_INFINITY

  for (const handle of getPointCloudHandles()) {
    const box = handle.boundingBox?.clone()

    if (!box) {
      continue
    }

    const object3D = handle.object3D
    if (object3D) {
      object3D.updateMatrixWorld(true)
      box.applyMatrix4(object3D.matrixWorld)
    }

    min = Math.min(min, box.min.z)
    max = Math.max(max, box.max.z)
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return null
  }

  return normalizeElevationBounds([min, max])
}

function resolvePointCloudAttributeBounds(attributeName: string): [number, number] | null {
  let min = Number.POSITIVE_INFINITY
  let max = Number.NEGATIVE_INFINITY

  for (const handle of getPointCloudHandles()) {
    const attribute = handle.attributes.attributes.find((item) => item.name === attributeName)
    const range = attribute?.range

    if (!range || range.length < 2) {
      continue
    }

    if (!Number.isFinite(range[0]) || !Number.isFinite(range[1])) {
      continue
    }

    min = Math.min(min, range[0])
    max = Math.max(max, range[1])
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return null
  }

  return normalizeElevationBounds([min, max])
}

function syncPointCloudPanelState() {
  const pointCloudPlugin = getPointCloudPlugin()
  const firstMaterial = getPointCloudMaterials()[0]
  const fallbackBounds = resolvePointCloudElevationBounds()
  const fallbackIntensityBounds = resolvePointCloudAttributeBounds('intensity')
  const fallbackGpsBounds = resolvePointCloudAttributeBounds('gps-time')

  if (!pointCloudPlugin && !firstMaterial) {
    pointSizingMode.value = 'adaptive'
    pointSize.value = DEFAULT_FIXED_POINT_SIZE
    pointRenderMode.value = 'rgba'
    pointCloudUseEDL.value = false
    pointCloudOpacity.value = 1
    pointElevationGradient.value = 'SPECTRAL'
    pointElevationBounds.value = [0, 1]
    pointElevationRange.value = [0, 1]
    pointIntensityGradient.value = 'SPECTRAL'
    pointIntensityBounds.value = [0, 1]
    pointIntensityRange.value = [0, 1]
    pointGpsGradient.value = 'SPECTRAL'
    pointGpsBounds.value = [0, 1]
    pointGpsRange.value = [0, 1]
    pointSingleColor.value = DEFAULT_SINGLE_COLOR_HEX
    return
  }

  const config = pointCloudPlugin?.getConfig()
  if (config) {
    pointSizingMode.value =
      config.pointSizeType === PointSizeType.ADAPTIVE ? 'adaptive' : 'fixed'
    pointSize.value =
      Number.isFinite(config.pointSize) && config.pointSize > 0
        ? config.pointSize
        : DEFAULT_FIXED_POINT_SIZE
    pointRenderMode.value = normalizePointRenderMode(config.activeAttributeName)
    pointCloudUseEDL.value = config.edl.enabled
  }

  if (firstMaterial) {
    pointShapeMode.value = firstMaterial.shape === PointShape.CIRCLE ? 'circle' : 'square'
    pointSizingMode.value =
      firstMaterial.pointSizeType === PointSizeType.ADAPTIVE ? 'adaptive' : 'fixed'
    pointSize.value =
      Number.isFinite(firstMaterial.size) && firstMaterial.size > 0
        ? firstMaterial.size
        : pointSize.value
    pointRenderMode.value = normalizePointRenderMode(firstMaterial.activeAttributeName)
    pointCloudOpacity.value =
      Number.isFinite(firstMaterial.opacity) && firstMaterial.opacity >= 0
        ? Math.min(1, Math.max(0, firstMaterial.opacity))
        : pointCloudOpacity.value
    pointSingleColor.value = `#${firstMaterial.color.getHexString()}`
  }

  const nextBounds = fallbackBounds ?? getMaterialElevationRange(firstMaterial) ?? [0, 1]
  const nextRange = getMaterialElevationRange(firstMaterial) ?? pointElevationRange.value
  const nextIntensityBounds =
    fallbackIntensityBounds ?? getMaterialIntensityRange(firstMaterial) ?? [0, 1]
  const nextIntensityRange = getMaterialIntensityRange(firstMaterial) ?? pointIntensityRange.value
  const nextGpsBounds = fallbackGpsBounds ?? [0, 1]

  pointElevationBounds.value = normalizeElevationBounds(nextBounds)
  pointElevationRange.value = clampElevationRange(nextRange, pointElevationBounds.value)
  pointIntensityBounds.value = normalizeElevationBounds(nextIntensityBounds)
  pointIntensityRange.value = clampElevationRange(nextIntensityRange, pointIntensityBounds.value)
  const normalizedGpsBounds = normalizeElevationBounds(nextGpsBounds)
  pointGpsBounds.value =
    normalizedGpsBounds[1] > normalizedGpsBounds[0] ? normalizedGpsBounds : [0, 1]

  if (firstMaterial?.gradient) {
    const gradientKey = resolveGradientKey(firstMaterial.gradient)
    if (pointRenderMode.value === 'elevation') {
      pointElevationGradient.value = gradientKey
    } else if (pointRenderMode.value === 'intensity') {
      pointIntensityGradient.value = gradientKey
    } else if (pointRenderMode.value === 'gps_time') {
      pointGpsGradient.value = gradientKey
    }
  }

  if (
    pointRenderMode.value === 'gps_time' &&
    firstMaterial?.activeSamplingRange &&
    firstMaterial.activeSamplingRange.length >= 2 &&
    Number.isFinite(firstMaterial.activeSamplingRange[0]) &&
    Number.isFinite(firstMaterial.activeSamplingRange[1])
  ) {
    pointGpsRange.value = denormalizeRangeWithinBounds(
      [firstMaterial.activeSamplingRange[0], firstMaterial.activeSamplingRange[1]],
      pointGpsBounds.value,
    )
  } else {
    const bounds = pointGpsBounds.value
    const current = pointGpsRange.value
    const hasBounds = bounds[1] > bounds[0]
    const hasRange = current[1] > current[0]
    const insideBounds = current[0] >= bounds[0] && current[1] <= bounds[1]
    pointGpsRange.value = hasBounds && hasRange && insideBounds ? current : [bounds[0], bounds[1]]
  }
}

function applyPointCloudVisualConfig() {
  const pointCloudPlugin = getPointCloudPlugin()
  const pointSizeType = resolvePointSizeType(pointSizingMode.value)
  const nextPointSize = Math.max(0.1, Number(pointSize.value) || DEFAULT_FIXED_POINT_SIZE)
  const nextOpacity = Math.min(1, Math.max(0, Number(pointCloudOpacity.value) || 0))
  const nextRenderMode = pointRenderMode.value
  const nextUseEDL = pointCloudUseEDL.value
  const nextElevationRange = clampElevationRange(pointElevationRange.value, pointElevationBounds.value)
  const nextGradient = Gradients[pointElevationGradient.value]
  const nextIntensityRange = clampElevationRange(pointIntensityRange.value, pointIntensityBounds.value)
  const nextIntensityGradient = Gradients[pointIntensityGradient.value]
  const nextGpsRange = clampElevationRange(pointGpsRange.value, pointGpsBounds.value)
  const nextGpsGradient = Gradients[pointGpsGradient.value]
  const nextGpsSamplingRange = normalizeRangeWithinBounds(nextGpsRange, pointGpsBounds.value)
  const nextSingleColor = new THREE.Color(pointSingleColor.value || DEFAULT_SINGLE_COLOR_HEX)
  const nextMaterialAttributeName =
    nextRenderMode === 'intensity' ? 'intensity gradient' : nextRenderMode

  pointSize.value = nextPointSize
  pointCloudOpacity.value = nextOpacity
  pointElevationRange.value = nextElevationRange
  pointIntensityRange.value = nextIntensityRange
  pointGpsRange.value = nextGpsRange

  pointCloudPlugin?.setConfig({
    pointSize: nextPointSize,
    pointSizeType,
    activeAttributeName: nextMaterialAttributeName,
  })
  pointCloudPlugin?.setEDL(nextUseEDL)

  const nextShape = resolvePointShape(pointShapeMode.value)

  for (const material of getPointCloudMaterials()) {
    material.size = nextPointSize
    material.pointSizeType = pointSizeType
    material.shape = nextShape
    material.activeAttributeName = nextMaterialAttributeName
    material.opacity = nextOpacity

    if (nextRenderMode === 'elevation') {
      material.gradient = nextGradient
      material.elevationRange = [...nextElevationRange]
      material.activeSamplingRange = [...nextElevationRange]
    }

    if (nextRenderMode === 'intensity') {
      material.gradient = nextIntensityGradient
      material.intensityRange = [...nextIntensityRange]
      material.activeSamplingRange = [...nextIntensityRange]
    }

    if (nextRenderMode === 'gps_time') {
      material.gradient = nextGpsGradient
      material.activeSamplingRange = [...nextGpsSamplingRange]
    }

    if (nextRenderMode === 'color') {
      material.color = nextSingleColor
    }
  }
}

function teardownBindings() {
  while (cleanupTasks.length > 0) {
    const task = cleanupTasks.pop()
    task?.()
  }
}

function syncPanelState() {
  const engineInstance = engine.value

  if (!engineInstance) {
    activeTool.value = null
    measureCount.value = 0
    clipCount.value = 0
    pointCloudAssetCount.value = 0
    spaceMouseNavigation.value = createSpaceMouseNavigationState(SPACEMOUSE_DEFAULT_PROFILE)
    syncPointCloudPanelState()
    return
  }

  activeTool.value = engineInstance.activeTool
  cameraMode.value = engineInstance.camera.mode
  controlMode.value = engineInstance.controls.activeMode
  syncSpaceMouseNavigationState()

  const measureService = getMeasureService()
  if (measureService) {
    measureType.value = measureService.getMeasureType()
    measureCount.value = measureService.getResults().length
  } else {
    measureCount.value = 0
  }

  const clipController = getClipController()
  if (clipController) {
    clipCount.value = clipController.volumes.filter(
      (volume) => volume.clipTask !== ClipTask.HIGHLIGHT,
    ).length
    clipSelectionMode.value =
      clipController.getSelectionMode?.() ??
      clipController.getClipMode?.() ??
      clipSelectionMode.value
    clipApplyMode.value =
      clipController.getClipApplyMode?.() ??
      clipApplyMode.value
    clipCanConfirm.value = clipController.getDraftState().canConfirm
  } else {
    clipCount.value = 0
    clipCanConfirm.value = false
  }

  pointCloudAssetCount.value = getPointCloudHandles().length
  syncPointBudgetState()
  syncPointCloudPanelState()
}

/**
 * 刷新"点数与预算"卡片：总点数取各资产 metadata.pointCount，激活点数/节点数取当前 LOD 结果，
 * 预算取引擎当前生效值。
 */
function syncPointBudgetState() {
  const engineInstance = engine.value
  if (!engineInstance) {
    pointCloudTotalPoints.value = 0
    pointCloudVisiblePoints.value = 0
    pointCloudVisibleNodes.value = 0
    pointBudget.value = 0
    return
  }

  let total = 0
  let visible = 0
  let nodes = 0
  for (const handle of getPointCloudHandles()) {
    total += handle.totalPointCount ?? 0
    visible += handle.visiblePointCount ?? 0
    nodes += handle.visibleNodeCount ?? 0
  }
  pointCloudTotalPoints.value = total
  pointCloudVisiblePoints.value = visible
  pointCloudVisibleNodes.value = nodes
  pointBudget.value = engineInstance.getPointBudget()
}

function bindPanelState() {
  const engineInstance = engine.value

  if (!engineInstance) {
    return
  }

  teardownBindings()
  ensureSpaceMouseDefaultProfile()

  const handleToolActivated = () => {
    syncPanelState()
  }

  const handleToolDeactivated = () => {
    syncPanelState()
  }

  const handleToolResultChanged = () => {
    syncPanelState()
  }

  const handleCameraModeChanged = () => {
    syncPanelState()
  }

  const handleControlsModeChanged = () => {
    syncPanelState()
  }

  engineInstance.events.on('tool.activated', handleToolActivated)
  engineInstance.events.on('tool.deactivated', handleToolDeactivated)
  engineInstance.events.on('tool.resultChanged', handleToolResultChanged)
  engineInstance.events.on('camera.modeChanged', handleCameraModeChanged)
  engineInstance.events.on('controls.modeChanged', handleControlsModeChanged)
  engineInstance.events.on('controls.spacemouseNavigationChanged', handleControlsModeChanged)

  cleanupTasks.push(() => engineInstance.events.off('tool.activated', handleToolActivated))
  cleanupTasks.push(() => engineInstance.events.off('tool.deactivated', handleToolDeactivated))
  cleanupTasks.push(() => engineInstance.events.off('tool.resultChanged', handleToolResultChanged))
  cleanupTasks.push(() => engineInstance.events.off('camera.modeChanged', handleCameraModeChanged))
  cleanupTasks.push(() => engineInstance.events.off('controls.modeChanged', handleControlsModeChanged))
  cleanupTasks.push(() =>
    engineInstance.events.off('controls.spacemouseNavigationChanged', handleControlsModeChanged),
  )

  const clipController = getClipController()
  const clipSubscription = clipController?.onChanged(() => {
    syncPanelState()
  })

  if (clipSubscription) {
    cleanupTasks.push(() => clipSubscription.dispose())
  }

  syncPanelState()
}

function clearPointCloudScene() {
  const pointCloudPlugin = getPointCloudPlugin()

  for (const handle of pointCloudPlugin?.getAll() ?? []) {
    pointCloudPlugin?.unmount(handle.id)
    pointCloudPlugin?.dispose(handle.id)
  }

  pointCloudLoadError.value = ''
  pointCloudAssetCount.value = 0
  syncPanelState()
}

async function loadPointCloud(inputPath?: string) {
  const engineInstance = engine.value

  if (!engineInstance || pointCloudLoading.value) {
    return
  }

  const rawPath = (inputPath ?? pointCloudInput.value).trim()
  if (!rawPath) {
    pointCloudLoadError.value = '请先选择包含 metadata.json 的本地目录。'
    return
  }

  pointCloudInput.value = rawPath
  pointCloudLoadError.value = ''
  pointCloudLoading.value = true

  try {
    clearPointCloudScene()

    const handle = await engineInstance.loadPointCloud({
      url: resolvePointCloudInputPath(rawPath),
      name: 'Point Cloud',
    })

    const fitBounds = engineInstance.context.sceneBounds.getWorldBoundingBox({
      ids: [handle.id],
      includeInvisible: true,
      purpose: 'fit',
    })
    engineInstance.camera.zoom = 1
    engineInstance.fitToAssets([handle.id])
    applyPointCloudOrbitZoomLimits(engineInstance, fitBounds)
    applyPointCloudVisualConfig()
    syncPanelState()
  } catch (error) {
    pointCloudLoadError.value = error instanceof Error ? error.message : '未知错误'
    console.error('[PotreeView] failed to load point cloud:', error)
  } finally {
    pointCloudLoading.value = false
  }
}

function activateMeasureTool(type: MeasureType) {
  const measureService = getMeasureService()

  measureService?.setMeasureType(type)
  engine.value?.activateTool('measure')
  syncPanelState()
}

function handleMeasureSelection(value: string) {
  activateMeasureTool(value as MeasureType)
}

function clearMeasureResults() {
  getMeasureService()?.clearResults()
  syncPanelState()
}

function activateClipTool(mode: ClipSelectionMode) {
  const clipController = getClipController()

  clipController?.setSelectionMode?.(mode)
  clipController?.setClipMode?.(mode)
  engine.value?.activateTool('clip')
  syncPanelState()
}

function handleClipSelection(value: string) {
  activateClipTool(value as ClipSelectionMode)
}

function setClipApplyMode(mode: ClipApplyMode) {
  const clipController = getClipController()

  clipController?.setClipApplyMode?.(mode)
  engine.value?.activateTool('clip')
  syncPanelState()
}

function handleClipApplySelection(value: string) {
  setClipApplyMode(value as ClipApplyMode)
}

function confirmClipDraft() {
  getClipController()?.confirmDraft?.()
  syncPanelState()
}

function clearClipVolumes() {
  getClipController()?.clearVolumes?.()
  syncPanelState()
}

function deactivateTool() {
  engine.value?.deactivateTool()
  syncPanelState()
}

function requiresPerspectiveControlMode(mode: ControlMode): boolean {
  return mode === 'first-person' || mode === 'legacy-first-person' || mode === 'fly'
}

function toggleCameraMode() {
  const engineInstance = engine.value

  if (!engineInstance) {
    return
  }

  const nextMode =
    cameraMode.value === CameraMode.Perspective
      ? CameraMode.Orthographic
      : CameraMode.Perspective

  if (nextMode === CameraMode.Orthographic && requiresPerspectiveControlMode(controlMode.value)) {
    engineInstance.controls.setMode('orbit')
  }

  engineInstance.camera.setMode(nextMode)
  syncPanelState()
}

function setView(preset: ViewPreset) {
  engine.value?.camera.setPresetView(preset)
}

function handleViewSelection(value: string) {
  setView(value as ViewPreset)
}

function fitAll() {
  engine.value?.fitAll()
}

function setControlMode(mode: ControlMode) {
  if (!engine.value) {
    return
  }

  if (requiresPerspectiveControlMode(mode) && engine.value.camera.mode !== CameraMode.Perspective) {
    engine.value.camera.setMode(CameraMode.Perspective)
  }

  engine.value.controls.setMode(mode)
  syncPanelState()
}

function handleControlModeSelection(value: string) {
  setControlMode(value as ControlMode)
}

function handleSpaceMouseSecondaryControlGroupSelection(payload: {
  groupId: string
  value: string
}) {
  applySpaceMouseSecondaryControlSelection(
    getSpaceMouseController(),
    payload.groupId,
    payload.value,
  )
  syncSpaceMouseNavigationState()
}

function setPointShapeMode(mode: PointShapeMode) {
  pointShapeMode.value = mode
  applyPointCloudVisualConfig()
  syncPanelState()
}

function handlePointShapeSelection(value: string) {
  setPointShapeMode(value as PointShapeMode)
}

function setPointSizingMode(mode: PointSizingMode) {
  pointSizingMode.value = mode
  if (mode === 'fixed' && !pointSizeCustomized) {
    pointSize.value = DEFAULT_FIXED_POINT_SIZE
  }

  applyPointCloudVisualConfig()
  syncPanelState()
}

function updatePointSize(value: number) {
  pointSizeCustomized = true
  pointSize.value = value
  applyPointCloudVisualConfig()
  syncPanelState()
}

function handlePointSizingSelection(value: string) {
  setPointSizingMode(value as PointSizingMode)
}

function setPointRenderMode(mode: PointRenderMode) {
  pointRenderMode.value = mode
  applyPointCloudVisualConfig()
  syncPanelState()
}

function handlePointRenderSelection(value: string) {
  setPointRenderMode(value as PointRenderMode)
}

function selectElevationGradient(value: ElevationGradientKey) {
  if (!(value in Gradients)) {
    return
  }

  pointElevationGradient.value = value
  applyPointCloudVisualConfig()
  syncPanelState()
}

function handleElevationGradientSelection(value: string) {
  selectElevationGradient(value as ElevationGradientKey)
}

function selectIntensityGradient(value: ElevationGradientKey) {
  if (!(value in Gradients)) {
    return
  }

  pointIntensityGradient.value = value
  applyPointCloudVisualConfig()
  syncPanelState()
}

function handleIntensityGradientSelection(value: string) {
  selectIntensityGradient(value as ElevationGradientKey)
}

function selectGpsGradient(value: ElevationGradientKey) {
  if (!(value in Gradients)) {
    return
  }

  pointGpsGradient.value = value
  applyPointCloudVisualConfig()
  syncPanelState()
}

function handleGpsGradientSelection(value: string) {
  selectGpsGradient(value as ElevationGradientKey)
}

function updateElevationRangeMin(value: number) {
  pointElevationRange.value = clampElevationRange(
    [Math.min(value, pointElevationRange.value[1]), pointElevationRange.value[1]],
    pointElevationBounds.value,
  )
  applyPointCloudVisualConfig()
  syncPanelState()
}

function updateElevationRangeMax(value: number) {
  pointElevationRange.value = clampElevationRange(
    [pointElevationRange.value[0], Math.max(value, pointElevationRange.value[0])],
    pointElevationBounds.value,
  )
  applyPointCloudVisualConfig()
  syncPanelState()
}

function updateIntensityRangeMin(value: number) {
  pointIntensityRange.value = clampElevationRange(
    [Math.min(value, pointIntensityRange.value[1]), pointIntensityRange.value[1]],
    pointIntensityBounds.value,
  )
  applyPointCloudVisualConfig()
  syncPanelState()
}

function updateIntensityRangeMax(value: number) {
  pointIntensityRange.value = clampElevationRange(
    [pointIntensityRange.value[0], Math.max(value, pointIntensityRange.value[0])],
    pointIntensityBounds.value,
  )
  applyPointCloudVisualConfig()
  syncPanelState()
}

function updateGpsRangeMin(value: number) {
  pointGpsRange.value = clampElevationRange(
    [Math.min(value, pointGpsRange.value[1]), pointGpsRange.value[1]],
    pointGpsBounds.value,
  )
  applyPointCloudVisualConfig()
  syncPanelState()
}

function updateGpsRangeMax(value: number) {
  pointGpsRange.value = clampElevationRange(
    [pointGpsRange.value[0], Math.max(value, pointGpsRange.value[0])],
    pointGpsBounds.value,
  )
  applyPointCloudVisualConfig()
  syncPanelState()
}

function updateSingleColor(colorHex: string) {
  pointSingleColor.value = colorHex || DEFAULT_SINGLE_COLOR_HEX
  applyPointCloudVisualConfig()
  syncPanelState()
}

function togglePointCloudEDL() {
  pointCloudUseEDL.value = !pointCloudUseEDL.value
  applyPointCloudVisualConfig()
  syncPanelState()
}

function updatePointCloudOpacity(value: number) {
  pointCloudOpacity.value = value
  applyPointCloudVisualConfig()
  syncPanelState()
}

const framesPerSecond = ref(0)
const frameTimeMs = ref(0)
let fpsRafId = 0
let fpsWindowStartedAt = 0
let fpsWindowFrames = 0

/** 按 500ms 窗口统计显示帧率与平均帧时间；on-demand 渲染下静止时会体现为显示器刷新率。 */
function startFpsCounter() {
  fpsWindowStartedAt = performance.now()
  fpsWindowFrames = 0
  const sample = (now: number) => {
    fpsWindowFrames += 1
    const elapsed = now - fpsWindowStartedAt
    if (elapsed >= 500) {
      const fps = (fpsWindowFrames * 1000) / elapsed
      framesPerSecond.value = Math.round(fps * 10) / 10
      frameTimeMs.value = Math.round((1000 / Math.max(fps, 0.001)) * 100) / 100
      fpsWindowStartedAt = now
      fpsWindowFrames = 0
    }
    fpsRafId = window.requestAnimationFrame(sample)
  }
  fpsRafId = window.requestAnimationFrame(sample)
}

function stopFpsCounter() {
  if (fpsRafId !== 0) {
    window.cancelAnimationFrame(fpsRafId)
    fpsRafId = 0
  }
}

let pointBudgetRefreshTimer: number | null = null

onMounted(async () => {
  await graphicsCard.runPreflight()
  try {
    await init()
  } catch (error) {
    graphicsCard.markError(toGraphicsRuntimeError(error))
    return
  }
  graphicsCard.markReady(actualBackend.value)
  applyPointDrawModeFromUrl()
  bindPanelState()
  await loadPointCloud()

  // 激活点数随相机/LOD 持续变化，事件不足以覆盖，这里做低频轮询刷新卡片。
  pointBudgetRefreshTimer = window.setInterval(syncPointBudgetState, 500)
  startFpsCounter()
})

onBeforeUnmount(() => {
  stopFpsCounter()
  if (pointBudgetRefreshTimer !== null) {
    window.clearInterval(pointBudgetRefreshTimer)
    pointBudgetRefreshTimer = null
  }
  teardownBindings()
})
</script>

<template>
  <div class="potree-page">
    <div class="main-render-container">
      <div class="pcontainer" ref="containerRef"></div>

      <div class="fps-badge" aria-live="off">
        <strong>{{ framesPerSecond.toFixed(1) }}</strong>
        <span>FPS</span>
        <em>{{ frameTimeMs.toFixed(2) }} ms</em>
      </div>

      <div class="screenshot-box">
        <button class="screenshot-button" type="button" :disabled="screenshotBusy" @click="captureScreenshot">
          {{ screenshotBusy ? '导出中…' : '导出截图' }}
        </button>
        <span v-if="screenshotNotice" class="screenshot-notice">{{ screenshotNotice }}</span>
      </div>

      <SceneControlPanel
        layout="sidebar"
        kicker="右侧面板"
        title="控制中心"
        :meta="`${activeToolLabel} / ${cameraModeLabel}`"
      >
        <SceneAssetLoaderCard
          v-model="pointCloudInput"
          badge="点云场景"
          :hint="pointCloudLoaderHint"
          :loading="pointCloudLoading"
          :can-clear="pointCloudAssetCount > 0"
          :directory-entry-names="['metadata.json']"
          @submit-load="loadPointCloud()"
          @clear-scene="clearPointCloudScene()"
        />

        <ScenePointBudgetCard
          :total-points="pointCloudTotalPoints"
          :visible-points="pointCloudVisiblePoints"
          :visible-node-count="pointCloudVisibleNodes"
          :budget="pointBudget"
          :draw-mode="pointDrawMode"
          :show-draw-mode="actualBackend === 'webgpu'"
          :grouped-draw-point-limit="groupedDrawPointLimit"
          :show-grouped-draw-point-limit="actualBackend === 'webgpu' && pointDrawMode === 'grouped'"
          :draw-call-color-enabled="drawCallColorEnabled"
          :show-draw-call-color="actualBackend === 'webgpu' && pointDrawMode === 'grouped'"
          @reload-config="reloadPointCloudDemo"
          @toggle-draw-call-color="setDrawCallColorEnabled"
        />

        <GraphicsBackendControlCard :card="graphicsCard" />

        <SceneStatusCard
          :badge="activeToolLabel"
          :items="statusItems"
          :hint="panelHint"
          action-label="停用当前工具"
          action-variant="neutral"
          :action-disabled="!activeTool"
          @action="deactivateTool()"
        />

        <ScenePointCloudCard
          :badge="pointCloudCardBadge"
          :shape-options="POINT_SHAPE_OPTIONS"
          :shape-value="pointShapeMode"
          :shape-label="activePointShapeDetail?.label ?? '方形'"
          :sizing-options="POINT_SIZING_OPTIONS"
          :sizing-value="pointSizingMode"
          :sizing-label="activePointSizingDetail?.label ?? '自适应'"
          :show-fixed-point-size="pointSizingMode === 'fixed'"
          :point-size="pointSize"
          :render-options="POINT_RENDER_OPTIONS"
          :render-value="pointRenderMode"
          :render-label="activePointRenderDetail?.label ?? 'RGB 模式'"
          :gradient-options="UI_GRADIENT_OPTIONS"
          :show-elevation-controls="pointRenderMode === 'elevation'"
          :elevation-gradient-value="pointElevationGradient"
          :elevation-gradient-label="activeElevationGradientDetail?.label ?? '光谱'"
          :elevation-gradient-css="activeElevationGradientCss"
          :elevation-bounds="pointElevationBounds"
          :elevation-range="pointElevationRange"
          :elevation-step="elevationRangeStep"
          :show-intensity-controls="pointRenderMode === 'intensity'"
          :intensity-gradient-value="pointIntensityGradient"
          :intensity-gradient-label="activeIntensityGradientDetail?.label ?? '光谱'"
          :intensity-gradient-css="activeIntensityGradientCss"
          :intensity-bounds="pointIntensityBounds"
          :intensity-range="pointIntensityRange"
          :intensity-step="intensityRangeStep"
          :show-gps-controls="pointRenderMode === 'gps_time'"
          :gps-gradient-value="pointGpsGradient"
          :gps-gradient-label="activeGpsGradientDetail?.label ?? '光谱'"
          :gps-gradient-css="activeGpsGradientCss"
          :gps-bounds="pointGpsBounds"
          :gps-range="pointGpsRange"
          :gps-step="gpsRangeStep"
          :show-single-color-controls="pointRenderMode === 'color'"
          :single-color="pointSingleColor"
          :single-color-swatches="POINT_COLOR_SWATCHES"
          :useEDL="pointCloudUseEDL"
          :opacity="pointCloudOpacity"
          @select-shape="handlePointShapeSelection"
          @select-sizing="handlePointSizingSelection"
          @update-point-size="updatePointSize"
          @select-render="handlePointRenderSelection"
          @select-elevation-gradient="handleElevationGradientSelection"
          @update-elevation-min="updateElevationRangeMin"
          @update-elevation-max="updateElevationRangeMax"
          @select-intensity-gradient="handleIntensityGradientSelection"
          @update-intensity-min="updateIntensityRangeMin"
          @update-intensity-max="updateIntensityRangeMax"
          @select-gps-gradient="handleGpsGradientSelection"
          @update-gps-min="updateGpsRangeMin"
          @update-gps-max="updateGpsRangeMax"
          @update-single-color="updateSingleColor"
          @toggle-edl="togglePointCloudEDL()"
          @update-opacity="updatePointCloudOpacity"
        />

        <SceneGroupCard title="工具类" subtitle="测量与裁切都收纳在这里" :badge="String(measureCount + clipCount)">
          <SceneMeasureCard
            :options="MEASURE_OPTIONS"
            :active-label="activeMeasureDetail?.label ?? '距离'"
            :active-value="measureType"
            :active="activeTool === 'measure'"
            :clear-disabled="measureCount === 0"
            @select="handleMeasureSelection"
            @clear="clearMeasureResults()"
          />

          <SceneClipCard
            :badge="clipCardBadge"
            :description="clipCardDescription"
            :selection-options="CLIP_SELECTION_OPTIONS"
            :selection-value="clipSelectionMode"
            :apply-options="CLIP_APPLY_OPTIONS"
            :apply-value="clipApplyMode"
            :active="activeTool === 'clip'"
            :live-box="isLiveBoxClipMode"
            :can-confirm="clipCanConfirm"
            :clear-disabled="clipCount === 0"
            @select-selection="handleClipSelection"
            @select-apply="handleClipApplySelection"
            @confirm="confirmClipDraft()"
            @clear="clearClipVolumes()"
          />
        </SceneGroupCard>

      <SceneViewCard
        :badge="cameraModeLabel"
        :is-perspective="cameraMode === CameraMode.Perspective"
        :options="VIEW_OPTIONS"
        :control-mode-options="CONTROL_MODE_OPTIONS"
        :active-control-mode="controlMode"
        :secondary-control-groups="
          controlMode === 'spacemouse' ? buildSpaceMouseSecondaryControlGroups(spaceMouseNavigation) : []
        "
        @toggle-mode="toggleCameraMode()"
        @fit="fitAll()"
        @set-view="handleViewSelection"
        @set-control-mode="handleControlModeSelection"
        @set-secondary-control-group-mode="handleSpaceMouseSecondaryControlGroupSelection"
      />
      </SceneControlPanel>
    </div>
  </div>
</template>

<style scoped>
.fps-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 10px;
  background: rgba(6, 14, 24, 0.72);
  border: 1px solid rgba(120, 156, 200, 0.28);
  color: #dff0ff;
  font-variant-numeric: tabular-nums;
  pointer-events: none;
  z-index: 5;
}

.fps-badge strong {
  font-size: 16px;
  font-weight: 700;
}

.fps-badge span {
  font-size: 11px;
  letter-spacing: 0.08em;
  color: #9fb7c8;
}

.fps-badge em {
  font-style: normal;
  font-size: 11px;
  color: #86a2b6;
}

@media (max-width: 1100px) {
  .fps-badge {
    top: 8px;
    left: 8px;
  }

}

.potree-page,
.main-render-container {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
}

.main-render-container {
  --panel-gap: 0px;
  display: grid;
  grid-template-columns: minmax(0, 7fr) minmax(0, 3fr);
  gap: var(--panel-gap);
  padding: var(--panel-gap);
  box-sizing: border-box;
  align-items: stretch;
}

.pcontainer {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  position: relative;
}

.screenshot-box {
  position: absolute;
  top: 58px;
  left: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 3;
}

.screenshot-button {
  padding: 5px 10px;
  border-radius: 8px;
  border: 1px solid rgba(120, 156, 200, 0.38);
  background: rgba(6, 14, 24, 0.78);
  color: #dff0ff;
  font-size: 12px;
  cursor: pointer;
}

.screenshot-button:disabled {
  opacity: 0.6;
  cursor: default;
}

.screenshot-notice {
  color: rgba(210, 232, 246, 0.85);
  font-size: 11px;
}
</style>
